import React from 'react';
import { Alert, Button, Flex, Typography, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { MessageList } from './MessageList';
import { MessageComposer } from './MessageComposer';
import NexusLogo from './NexusLogo';
import { NexusConversationState, SendMessagePayload } from './types';

interface NexusChatProps {
  state: NexusConversationState;
  onSendMessage: (payload: SendMessagePayload) => Promise<void>;
  prefillText?: string;
  /** Absent where the surrounding chrome already closes the chat, as a Drawer does. */
  onClose?: () => void;
}

export const NEXUS_CHAT_CLOSE_LABEL = 'Close Nexus';

function NexusChatHeader({ onClose }: { onClose?: () => void }) {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        padding: token.padding,
        borderBottom: `1px solid ${token.colorBorder}`,
      }}
    >
      <Flex align="flex-start" gap={token.marginXS}>
        <Flex
          align="center"
          justify="center"
          style={{
            width: token.sizeLG,
            height: token.sizeLG,
            borderRadius: '50%',
            backgroundColor: token.colorPrimaryBg,
            flexShrink: 0,
          }}
        >
          <NexusLogo size={token.size} />
        </Flex>
        <Flex vertical flex={1} gap={token.sizeXXS}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Ask Nexus
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Your AI Integration Assistant
          </Typography.Text>
        </Flex>
        {onClose && (
          <Button
            type="text"
            size="small"
            aria-label={NEXUS_CHAT_CLOSE_LABEL}
            icon={<CloseOutlined style={{ color: token.colorPrimary }} />}
            onClick={onClose}
          />
        )}
      </Flex>
    </div>
  );
}

export function NexusChat({ state, onSendMessage, prefillText, onClose }: NexusChatProps) {
  const { token } = theme.useToken();

  return (
    <Flex
      vertical
      style={{
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <NexusChatHeader onClose={onClose} />

      {state.error && <Alert message={state.error} type="error" closable style={{ margin: token.margin }} />}

      <MessageList messages={state.messages} isLoading={state.isLoading} />

      <MessageComposer onSendMessage={onSendMessage} disabled={state.isLoading} prefillText={prefillText} />
    </Flex>
  );
}
